import React from 'react';
import type { WizardStep, WizardLayout, WizardStepStatus } from '../../types/WizardTypes';

interface WizardProgressIndicatorProps {
  steps: WizardStep[];
  currentStep: number;
  stepCompleted: Record<string, boolean>;
  stepErrors: Record<string, string[]>;
  onStepClick?: (stepIndex: number) => void;
  layout: WizardLayout;
}

/**
 * WizardProgressIndicator - Visual progress indicator for form wizard
 * Matches the ProductWizard.tsx stepper design exactly
 */
export const WizardProgressIndicator: React.FC<WizardProgressIndicatorProps> = ({
  steps,
  currentStep,
  stepCompleted,
  stepErrors,
  onStepClick
}) => {
  
  // Handle step click - allow navigation to current step or previous steps
  const handleStepClick = (stepIndex: number) => {
    if (onStepClick) {
      onStepClick(stepIndex);
    }
  };

  // Step configuration matching ProductWizard design
  const stepConfig = steps.map((step, index) => ({
    id: index + 1,
    title: step.title,
    subtitle: step.subtitle || ''
  }));

  return (
    <div className="tw-flex tw-shrink-0 tw-px-4 md:tw-px-6 tw-py-4 tw-bg-gray-50 dark:tw-bg-[#1C252E] tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-shadow-sm">
      <div className="tw-relative tw-flex tw-items-center tw-justify-between tw-w-full tw-max-w-2xl tw-mx-auto">
        {/* Progress Line */}
        <div className="tw-absolute tw-left-0 tw-top-[15px] tw-w-full tw-h-0.5 tw-bg-gray-200 dark:tw-bg-gray-700 tw-z-0"></div>
        <div
          className="tw-absolute tw-left-0 tw-top-[15px] tw-h-0.5 tw-bg-primary tw-z-0 tw-transition-all tw-duration-300"
          style={{ width: `${currentStep * 25}%` }}
        ></div>

        {/* Step Indicators */}
        {stepConfig.map((step) => (
          <div
            key={step.id}
            className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-group tw-cursor-pointer"
            onClick={() => handleStepClick(step.id - 1)}
          >
            <div
              className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-ring-2 tw-ring-white/80 dark:tw-ring-gray-900/80 tw-ring-offset-1 tw-ring-offset-transparent tw-shadow-sm tw-z-20 tw-transition-all ${
                currentStep >= step.id - 1
                  ? "tw-bg-primary tw-text-white"
                  : "tw-bg-gray-200 dark:tw-bg-gray-700 tw-text-gray-500 dark:tw-text-gray-400"
              }`}
            >
              {step.id}
            </div>
            <span
              className={`tw-text-xs tw-font-medium tw-text-center tw-transition-colors ${
                step.id <= 2 ? "" : "hidden sm:block"
              } ${
                currentStep >= step.id - 1
                  ? "tw-text-primary tw-font-bold"
                  : "tw-text-gray-500 dark:tw-text-gray-400"
              }`}
            >
              {step.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};